import { useId } from 'react';
import { Checkbox as Base } from '@base-ui/react/checkbox';
import { Check, Minus } from 'lucide-react';
import { cn } from './cn';
import { focusRing } from './focus';
import { Icon } from './icons';

/** A labelled checkbox (Base UI): a real checkbox role, `mixed` when indeterminate, 44 px of row on touch. */
export function Checkbox({
  label,
  checked,
  onCheckedChange,
  disabled,
  indeterminate,
  hideLabel,
  className,
}: {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  indeterminate?: boolean;
  /** The label names the box for assistive technology only (a row's checkbox in a table). */
  hideLabel?: boolean;
  className?: string;
}) {
  const id = useId();
  return (
    // Not a <label>: it would also click Base UI's hidden input and toggle twice. The whole row (44 px on touch) toggles
    // on a press, except on the box and its hidden input, which toggle by themselves (P12a).
    <span
      data-hit-area=""
      onClick={(e) => {
        if (disabled || (e.target instanceof Element && e.target.closest('[role="checkbox"], input'))) return;
        onCheckedChange(!checked);
      }}
      className={cn('inline-flex items-center gap-2 font-sans text-sm text-fg pointer-coarse:min-h-11', className)}
    >
      <Base.Root
        aria-labelledby={id}
        checked={checked}
        indeterminate={indeterminate}
        disabled={disabled}
        onCheckedChange={(v) => onCheckedChange(v)}
        className={cn(
          'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border-strong bg-surface text-accent-fg transition-colors duration-(--duration-fast) data-[checked]:border-accent data-[checked]:bg-accent data-[indeterminate]:border-accent data-[indeterminate]:bg-accent data-[disabled]:opacity-50',
          focusRing,
        )}
      >
        <Base.Indicator keepMounted={false}>
          <Icon icon={indeterminate ? Minus : Check} size={12} />
        </Base.Indicator>
      </Base.Root>
      <span id={id} className={hideLabel ? 'sr-only' : disabled ? undefined : 'cursor-default'}>
        {label}
      </span>
    </span>
  );
}
